import React, { useState, useEffect, useCallback } from 'react'
import {
    Alert,
    AlertTitle,
    Avatar,
    Button,
    Card,
    CardContent,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    Divider,
    Grid,
    Icon,
    IconButton,
    MenuItem,
    Slide,
    Snackbar,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
    Typography,
    Chip,
    InputAdornment,
    Checkbox,
} from '@mui/material'
import { Box, styled } from '@mui/system'
import { H4 } from 'app/components/Typography'
import { Formik } from 'formik'
import useAuth from 'app/hooks/useAuth'
import { API_URI } from 'config'
import Axios from 'axios'
import { LoadingButton } from '@mui/lab'
import SendIcon from '@mui/icons-material/Send'
import EditIcon from '@mui/icons-material/Edit'
import DeleteIcon from '@mui/icons-material/Delete'
import CloudUploadIcon from '@mui/icons-material/CloudUpload'
import PersonAddIcon from '@mui/icons-material/PersonAdd'
import EmailIcon from '@mui/icons-material/Email'
import PhoneIcon from '@mui/icons-material/Phone'
import WorkIcon from '@mui/icons-material/Work'
import SecurityIcon from '@mui/icons-material/Security'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import VpnKeyIcon from '@mui/icons-material/VpnKey'
import { ENV } from 'config'
import { validateImageFile } from 'app/utils/fileValidation'
import { getAccessToken } from 'app/auth/authStorage'
import { getApiErrorMessage } from 'app/contexts/JWTAuthContext'
import useSaAccess from 'app/hooks/useSaAccess'
import {
    SA_SECTIONS,
    accessLevelsForSection,
    defaultSectionAccessForSecondary,
    fullWriteAccess,
    normalizeClientIdList,
    normalizeSectionAccess,
    resolveSaSectionAccess,
} from 'app/auth/saAccess'

const VisuallyHiddenInput = styled('input')({
    clip: 'rect(0 0 0 0)',
    clipPath: 'inset(50%)',
    height: 1,
    overflow: 'hidden',
    position: 'absolute',
    bottom: 0,
    left: 0,
    whiteSpace: 'nowrap',
    width: 1,
})

const Transition = React.forwardRef(function Transition(props, ref) {
    return <Slide direction="up" ref={ref} {...props} />
})

const DialogFooter = styled(Box)(() => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
}))

const DialogHeader = styled(DialogFooter)(({ theme }) => ({
    padding: '16px 24px',
    backgroundColor: theme.palette.primary.main,
    borderRadius: '8px 8px 0 0',
}))

const StyledCard = styled(Card)(({ theme }) => ({
    borderRadius: '8px',
    boxShadow: 'none',
    border: '1px solid',
    borderColor: theme.palette.divider,
    overflow: 'hidden',
}))

const StyledTableRow = styled(TableRow)(({ theme }) => ({
    '&:nth-of-type(even)': {
        backgroundColor: theme.palette.action.hover,
    },
}))

const ProfileSection = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: theme.spacing(3),
    marginBottom: theme.spacing(2),
    borderRadius: theme.spacing(1),
    backgroundColor: theme.palette.grey[50],
    border: `1px solid ${theme.palette.divider}`,
}))

const FormSection = styled(Box)(({ theme }) => ({
    marginBottom: theme.spacing(3),
    '& .MuiTextField-root': {
        marginBottom: theme.spacing(2),
    },
}))

const SectionTitle = styled(Typography)(({ theme }) => ({
    fontWeight: 600,
    color: theme.palette.primary.main,
    marginBottom: theme.spacing(2),
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
}))

const ROLE_OPTIONS = [
    {
        value: 'ADMIN',
        label: 'System Manager',
        helperText:
            'The user will be added as primary admin for the portal and will have access to all the Locations and members.',
    },
    {
        value: 'STORE_MANAGER',
        label: 'Location Manager',
        helperText: 'Users will have access to selected locations only.',
    },
]

const ROLE_OPTIONS_SUPER_ADMIN = [
    {
        value: 'master',
        label: 'Super Admin',
        role: 'SA',
        permission: ['master'],
        helperText:
            'The user will be added as super admin for the portal and will have access to all the functions of the portal.',
    },
    {
        value: 'secondary',
        label: 'Secondary Admin',
        role: 'SA',
        permission: ['secondary'],
        helperText:
            'Secondary admins only see the dashboard sections you grant below, with read-only or read & write access.',
    },
]

const DEFAULT_SUPER_ADMIN_OPTION = ROLE_OPTIONS_SUPER_ADMIN[0]
const DEFAULT_SUPER_ADMIN_PERMISSION = DEFAULT_SUPER_ADMIN_OPTION.value

const INITIAL_STATE = {
    title: '',
    firstName: '',
    lastName: '',
    companyPosition: '',
    email: '',
    phone: '',
    role: '',
    permission: [],
    storeIds: [],
    sectionAccess: defaultSectionAccessForSecondary(),
    allowedClientIds: [],
}

/** Seed create-form defaults so the Role select UI matches submitted values. */
const getCreateInitialValues = (isSaManager, { asMaster = true } = {}) => {
    if (!isSaManager) return { ...INITIAL_STATE }
    if (asMaster) {
        return {
            ...INITIAL_STATE,
            role: DEFAULT_SUPER_ADMIN_OPTION.role,
            permission: [...DEFAULT_SUPER_ADMIN_OPTION.permission],
            sectionAccess: fullWriteAccess(),
            allowedClientIds: [],
        }
    }
    const secondary = ROLE_OPTIONS_SUPER_ADMIN.find(
        (r) => r.value === 'secondary'
    )
    return {
        ...INITIAL_STATE,
        role: secondary.role,
        permission: [...secondary.permission],
        sectionAccess: defaultSectionAccessForSecondary(),
        allowedClientIds: [],
    }
}

const LEVEL_CHIP_COLOR = {
    none: 'default',
    read: 'info',
    login: 'secondary',
    write: 'success',
}

const formatSectionAccessChips = (admin) => {
    if (admin?.permission?.includes('master')) {
        return (
            <Chip
                size="small"
                label="Full access"
                color="success"
                sx={chipWrapSx}
            />
        )
    }
    const map = resolveSaSectionAccess({
        role: 'SA',
        permission: admin?.permission,
        sectionAccess: admin?.sectionAccess,
    })
    const clientIds = normalizeClientIdList(admin?.allowedClientIds)
    const clientsLevel = map.clients || 'none'
    return (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {SA_SECTIONS.map((section) => {
                const level = map[section.key] || 'none'
                if (level === 'none') return null
                const levelLabel =
                    accessLevelsForSection(section.key).find(
                        (l) => l.value === level
                    )?.label || level
                return (
                    <Chip
                        key={section.key}
                        size="small"
                        color={LEVEL_CHIP_COLOR[level] || 'default'}
                        label={`${section.label}: ${levelLabel}`}
                        sx={chipWrapSx}
                    />
                )
            })}
            {clientsLevel !== 'none' && (
                <Chip
                    size="small"
                    color="warning"
                    label={
                        clientIds.length
                            ? `${clientIds.length} client${
                                  clientIds.length === 1 ? '' : 's'
                              }`
                            : 'No clients selected'
                    }
                    sx={chipWrapSx}
                />
            )}
        </Box>
    )
}

const RoleDataHeader = [
    'Admin Details',
    'Contact Information',
    'Role',
    'Store Access',
    'Actions',
]

const RoleDataHeaderSuperAdmin = [
    'Admin Details',
    'Contact Information',
    'Role',
    'Permissions',
    'Actions',
]


const chipWrapSx = {
    height: 'auto',
    py: 0.4,
    maxWidth: '100%',
    '& .MuiChip-label': {
        whiteSpace: 'normal',
        lineHeight: 1.3,
        wordBreak: 'break-word',
        py: 0.25,
    },
}

export const formatStoreLabel = (store) => {
    const costCenterNumber = store?.costCenterId?.cost_center_number
    const name = store?.name ?? ''
    if (costCenterNumber && name) return `${costCenterNumber} - ${name}`
    if (costCenterNumber) return String(costCenterNumber)
    return name || 'N/A'
}

const AuthorizeAdmin = () => {
    const [adminDialog, setAdminDialog] = useState(false)
    const [adminInfo, setAdminInfo] = useState([])
    const [imageUrl, setImageUrl] = useState({})
    const [deleteDialog, setDeleteDialog] = useState(false)
    const [resendCredentialsDialog, setResendCredentialsDialog] =
        useState(false)
    const [loading, setLoading] = useState(false)
    const [uploading, setUploading] = useState(false)
    const [message, setMessage] = useState('')
    const [success, setSuccess] = useState(false)
    const [open, setOpen] = useState(false)
    const { user } = useAuth()
    const { canWrite: canWriteSaSection, isMaster } = useSaAccess()

    const [initialValues, setInitialValues] = useState(INITIAL_STATE)
    const [storeOptions, setStoreOptions] = useState([])
    const [storesLoading, setStoresLoading] = useState(false)
    const [clientOptions, setClientOptions] = useState([])
    const [clientsLoading, setClientsLoading] = useState(false)
    const isAdminUser = user?.role === 'ADMIN'
    const canManageAdmins =
        isAdminUser || (user?.role === 'SA' && canWriteSaSection('settings'))
    const canConfigureSaRoles = user?.role === 'SA' && canManageAdmins
    const saRoleOptions = isMaster
        ? ROLE_OPTIONS_SUPER_ADMIN
        : ROLE_OPTIONS_SUPER_ADMIN.filter((r) => r.value === 'secondary')

    const deleteHandler = useCallback(async () => {
        setLoading(true)
        try {
            const { data } = await Axios.delete(
                `${API_URI}/api/admin/delete/secondary/${initialValues?._id}`,
                {}
            )
            const { success, user } = data
            setSuccess(true)
            setMessage('Admin removed successfully')
            setLoading(false)
            setOpen(true)
            if (success && user) {
                setDeleteDialog(false)
                getSupportAdmin()
            }
        } catch (error) {
            setMessage(getApiErrorMessage(error))
            setSuccess(false)
            setLoading(false)
            setOpen(true)
        }
    }, [initialValues?._id])

    const resendCredentialsHandler = useCallback(async () => {
        if (!initialValues?._id) return
        setLoading(true)
        try {
            const { data } = await Axios.post(
                `${API_URI}/api/admin/secondary/resend-credentials/${initialValues._id}`,
                {}
            )
            setSuccess(Boolean(data?.success))
            setMessage(
                data?.message ||
                    'A new password has been emailed to the admin.'
            )
            setOpen(true)
            if (data?.success) {
                setResendCredentialsDialog(false)
                setInitialValues(INITIAL_STATE)
            }
        } catch (error) {
            setMessage(getApiErrorMessage(error))
            setSuccess(false)
            setOpen(true)
        } finally {
            setLoading(false)
        }
    }, [initialValues?._id])

    const handleSubmit = async (values, { isSubmitting, resetForm }) => {
        setLoading(true)

        try {
            const endpoint = `${API_URI}/api/admin/secondary/auth/register`

            const { data } = await Axios.post(
                endpoint,
                { ...values, imageUrl },
                {}
            )

            const { message, success: responseSuccess } = data
            setMessage(
                message ||
                    (values._id
                        ? 'Admin updated successfully'
                        : 'Admin added successfully')
            )
            setSuccess(true)
            setLoading(false)
            setOpen(true)

            if (responseSuccess !== false) {
                setAdminDialog(false)
                resetForm()
                setInitialValues(
                    getCreateInitialValues(canConfigureSaRoles, {
                        asMaster: isMaster,
                    })
                )
                setImageUrl({})
                getSupportAdmin()
            }
        } catch (error) {
            setLoading(false)
            setMessage(getApiErrorMessage(error))
            setSuccess(false)
            setOpen(true)
        }
    }

    const getSupportAdmin = useCallback(async () => {
        try {
            const { data } = await Axios.get(
                `${API_URI}/api/admin/get-supporting-admin/${user?.clubId?._id}`,
                {}
            )
            const { adminData } = data
            setAdminInfo(adminData)
            setImageUrl(adminData?.avatar)
        } catch (error) {
            // Error handled silently
        }
    }, [user?.clubId])

    const getStores = useCallback(async () => {
        if (!isAdminUser || !user?.clubId?._id) return
        setStoresLoading(true)
        try {
            const { data } = await Axios.get(`${API_URI}/api/store/list`, {
                params: { clubId: user?.clubId?._id },
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${getAccessToken()}`,
                },
            })
            const list = Array.isArray(data?.stores)
                ? data.stores
                : Array.isArray(data?.data?.stores)
                  ? data.data.stores
                  : Array.isArray(data)
                    ? data
                    : []
            setStoreOptions(list)
        } catch (error) {
            setStoreOptions([])
        } finally {
            setStoresLoading(false)
        }
    }, [isAdminUser, user?.clubId?._id])

    const getClientOptions = useCallback(async () => {
        if (!canConfigureSaRoles) return
        setClientsLoading(true)
        try {
            const { data } = await Axios.get(`${API_URI}/api/clubs`, {})
            const list = Array.isArray(data) ? data : []
            setClientOptions(list)
        } catch (error) {
            setClientOptions([])
        } finally {
            setClientsLoading(false)
        }
    }, [canConfigureSaRoles])

    const onImageHandler = async (e) => {
        e.preventDefault()
        const file = e.target.files[0]
        if (!file) return

        const check = validateImageFile(file)
        if (!check.ok) {
            setMessage(check.message)
            setSuccess(false)
            setOpen(true)
            e.target.value = ''
            return
        }

        const formData = new FormData()
        formData.append('profile_admin', file)

        try {
            setUploading(true)
            const { data } = await Axios.post(
                `${API_URI}/api/admin/upload-admin/profile`,
                formData,
                {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                    },
                }
            )
            setUploading(false)
            setImageUrl(data)
        } catch (error) {
            setUploading(false)
            setMessage(getApiErrorMessage(error))
            setSuccess(false)
            setOpen(true)
        }
    }

    useEffect(() => {
        getSupportAdmin()
    }, [getSupportAdmin])

    useEffect(() => {
        if (adminDialog && isAdminUser) {
            getStores()
        }
    }, [adminDialog, isAdminUser, getStores])

    useEffect(() => {
        if (adminDialog && canConfigureSaRoles) {
            getClientOptions()
        }
    }, [adminDialog, canConfigureSaRoles, getClientOptions])

    const getRoleDisplay = (permission, role) => {
        const isPrimary =
            permission?.includes('club_owner') ||
            (role === 'SA' && permission?.includes('master'))
        return (
            <Chip
                label={isPrimary ? 'PRIMARY' : 'SECONDARY'}
                color={isPrimary ? 'primary' : 'default'}
                size="small"
                icon={<SecurityIcon sx={{ fontSize: 16 }} />}
            />
        )
    }

    const SupportAdminList = () => {
        if (!adminInfo || adminInfo.length === 0) {
            return (
                <Box
                    display="flex"
                    flexDirection="column"
                    alignItems="center"
                    justifyContent="center"
                    minHeight="200px"
                    bgcolor="grey.50"
                    borderRadius={2}
                    m={2}
                >
                    <SecurityIcon
                        sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }}
                    />
                    <Typography
                        variant="h6"
                        color="text.secondary"
                        gutterBottom
                    >
                        No administrators found
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Add your first admin to get started
                    </Typography>
                </Box>
            )
        }

        return (
            <StyledCard>
                <Table>
                    <TableHead>
                        <TableRow sx={{ bgcolor: 'primary.main' }}>
                            {user?.role === 'SA'
                                ? RoleDataHeaderSuperAdmin.map(
                                      (header, index) => (
                                          <TableCell
                                              key={index}
                                              sx={{
                                                  color: 'white',
                                                  fontWeight: 600,
                                              }}
                                              align={
                                                  index ===
                                                  RoleDataHeader.length - 1
                                                      ? 'center'
                                                      : 'center'
                                              }
                                          >
                                              {header}
                                          </TableCell>
                                      )
                                  )
                                : RoleDataHeader.map((header, index) => (
                                      <TableCell
                                          key={index}
                                          sx={{
                                              color: 'white',
                                              fontWeight: 600,
                                          }}
                                          align={
                                              index ===
                                              RoleDataHeader.length - 1
                                                  ? 'center'
                                                  : 'center'
                                          }
                                      >
                                          {header}
                                      </TableCell>
                                  ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {adminInfo?.map((item, ind) => (
                            <StyledTableRow key={ind}>
                                <TableCell sx={{ pl: 2 }}>
                                    <Box
                                        display="flex"
                                        alignItems="center"
                                        gap={2}
                                    >
                                        <Avatar
                                            src={item?.avatar?.Location}
                                            sx={{ width: 40, height: 40 }}
                                        >
                                            {`${item?.firstName?.[0] || ''}${item?.lastName?.[0] || ''}`}
                                        </Avatar>
                                        <Box>
                                            <Typography
                                                variant="subtitle2"
                                                fontWeight={600}
                                            >
                                                {item?.title} {item?.firstName}{' '}
                                                {item?.lastName}
                                            </Typography>
                                            <Typography
                                                variant="body2"
                                                color="text.secondary"
                                            >
                                                {item?.companyPosition}
                                            </Typography>
                                        </Box>
                                    </Box>
                                </TableCell>
                                <TableCell sx={{ pl: 2 }}>
                                    <Box
                                        display="flex"
                                        flexDirection="column"
                                        gap={0.5}
                                    >
                                        <Box
                                            display="flex"
                                            alignItems="center"
                                            gap={1}
                                        >
                                            <EmailIcon
                                                sx={{
                                                    fontSize: 14,
                                                    color: 'text.secondary',
                                                }}
                                            />
                                            <Typography variant="body2">
                                                {item.email}
                                            </Typography>
                                        </Box>
                                        <Box
                                            display="flex"
                                            alignItems="center"
                                            gap={1}
                                        >
                                            <PhoneIcon
                                                sx={{
                                                    fontSize: 14,
                                                    color: 'text.secondary',
                                                }}
                                            />
                                            <Typography variant="body2">
                                                {item.phone}
                                            </Typography>
                                        </Box>
                                    </Box>
                                </TableCell>
                                <TableCell sx={{ pl: 2 }}>
                                    {getRoleDisplay(item.permission, item.role)}
                                </TableCell>

                                <TableCell
                                    sx={{
                                        pl: 2,
                                        minWidth: 280,
                                        maxWidth: 360,
                                        verticalAlign: 'top',
                                    }}
                                >
                                    {canConfigureSaRoles ||
                                    user?.role === 'SA' ? (
                                        formatSectionAccessChips(item)
                                    ) : (
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                flexDirection: 'column',
                                                gap: 0.75,
                                            }}
                                        >
                                            {item?.permission?.includes(
                                                'club_owner'
                                            ) &&
                                            item?.role !== 'STORE_MANAGER' ? (
                                                <Chip
                                                    size="small"
                                                    label="Primary Holder"
                                                    sx={chipWrapSx}
                                                />
                                            ) : (
                                                <Box
                                                    sx={{
                                                        display: 'flex',
                                                        flexDirection: 'column',
                                                        alignItems:
                                                            'flex-start',
                                                        gap: 0.5,
                                                    }}
                                                >
                                                    <Chip
                                                        size="small"
                                                        label="Location Manager"
                                                        sx={chipWrapSx}
                                                    />
                                                    {item?.role === 'ADMIN' && (
                                                        <Typography
                                                            variant="caption"
                                                            color="text.secondary"
                                                        >
                                                            (All Locations
                                                            Access)
                                                        </Typography>
                                                    )}
                                                </Box>
                                            )}
                                            {item?.storeIds?.length > 0 && (
                                                <Box
                                                    sx={{
                                                        display: 'flex',
                                                        flexWrap: 'wrap',
                                                        gap: 0.5,
                                                    }}
                                                >
                                                    {item.storeIds.map(
                                                        (store, idx) => (
                                                            <Chip
                                                                key={
                                                                    store?._id ??
                                                                    `${store?.name ?? 'store'}-${idx}`
                                                                }
                                                                size="small"
                                                                label={formatStoreLabel(
                                                                    store
                                                                )}
                                                                sx={chipWrapSx}
                                                            />
                                                        )
                                                    )}
                                                </Box>
                                            )}
                                        </Box>
                                    )}
                                </TableCell>

                                <TableCell sx={{ pl: 2 }} align="center">
                                    <Tooltip title="Edit Admin" placement="top">
                                        <IconButton
                                            color="primary"
                                            disabled={!canManageAdmins}
                                            onClick={() => {
                                                setInitialValues({
                                                    ...item,
                                                    storeIds: Array.isArray(
                                                        item?.storeIds
                                                    )
                                                        ? item.storeIds
                                                              .map((store) =>
                                                                  typeof store ===
                                                                  'string'
                                                                      ? store
                                                                      : store?._id
                                                              )
                                                              .filter(Boolean)
                                                        : [],
                                                    sectionAccess:
                                                        item?.role === 'SA'
                                                            ? normalizeSectionAccess(
                                                                  item.sectionAccess,
                                                                  {
                                                                      isMaster:
                                                                          item?.permission?.includes(
                                                                              'master'
                                                                          ),
                                                                  }
                                                              )
                                                            : defaultSectionAccessForSecondary(),
                                                    allowedClientIds:
                                                        item?.role === 'SA'
                                                            ? normalizeClientIdList(
                                                                  item.allowedClientIds
                                                              )
                                                            : [],
                                                })
                                                setImageUrl(item?.avatar)
                                                setAdminDialog(true)
                                            }}
                                        >
                                            <EditIcon />
                                        </IconButton>
                                    </Tooltip>
                                    <Tooltip
                                        title="Resend username & password"
                                        placement="top"
                                    >
                                        <IconButton
                                            color="secondary"
                                            disabled={!canManageAdmins}
                                            onClick={() => {
                                                setInitialValues(item)
                                                setResendCredentialsDialog(true)
                                            }}
                                        >
                                            <VpnKeyIcon />
                                        </IconButton>
                                    </Tooltip>
                                    {!item?.permission?.includes(
                                        'club_owner'
                                    ) &&
                                        item?.role !== 'SA' && (
                                            <Tooltip
                                                title="Delete Admin"
                                                placement="top"
                                            >
                                                <IconButton
                                                    color="error"
                                                    onClick={() => {
                                                        setInitialValues(item)
                                                        setDeleteDialog(true)
                                                    }}
                                                >
                                                    <DeleteIcon />
                                                </IconButton>
                                            </Tooltip>
                                        )}
                                </TableCell>
                            </StyledTableRow>
                        ))}
                    </TableBody>
                </Table>
            </StyledCard>
        )
    }

    const AddAdminFormDialog = () => {
        const isEditing = Boolean(initialValues._id)

        return (
            <Dialog
                onClose={() => setAdminDialog(false)}
                open={adminDialog}
                fullWidth
                maxWidth="md"
                PaperProps={{ sx: { borderRadius: 2 } }}
            >
                <DialogHeader>
                    <H4
                        sx={{
                            m: 0,
                            color: '#fff',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                        }}
                    >
                        <PersonAddIcon />
                        {isEditing ? 'Edit Admin' : 'Add New Admin'}
                    </H4>
                    <IconButton onClick={() => setAdminDialog(false)}>
                        <Icon sx={{ color: '#fff' }}>close</Icon>
                    </IconButton>
                </DialogHeader>

                <Box p={3}>
                    <Formik
                        initialValues={initialValues}
                        onSubmit={handleSubmit}
                        enableReinitialize
                    >
                        {({
                            values,
                            handleChange,
                            handleSubmit,
                            setFieldValue,
                        }) => (
                            <Box component="form" onSubmit={handleSubmit}>
                                {/* Profile Picture Section */}
                                <ProfileSection>
                                    <SectionTitle variant="subtitle1">
                                        <CloudUploadIcon />
                                        Profile Picture
                                    </SectionTitle>

                                    {imageUrl?.Location ? (
                                        <Box
                                            display="flex"
                                            flexDirection="column"
                                            alignItems="center"
                                            gap={2}
                                        >
                                            <Avatar
                                                alt={`${values.firstName} ${values.lastName}`}
                                                src={imageUrl.Location}
                                                sx={{ width: 96, height: 96 }}
                                            />
                                            <Button
                                                component="label"
                                                variant="outlined"
                                                size="small"
                                                startIcon={<CloudUploadIcon />}
                                            >
                                                Change Picture
                                                <VisuallyHiddenInput
                                                    type="file"
                                                    accept="image/png, image/gif, image/jpeg"
                                                    onChange={onImageHandler}
                                                />
                                            </Button>
                                        </Box>
                                    ) : (
                                        <Button
                                            component="label"
                                            variant="contained"
                                            startIcon={<CloudUploadIcon />}
                                        >
                                            Upload Profile Picture
                                            <VisuallyHiddenInput
                                                type="file"
                                                accept="image/png, image/gif, image/jpeg"
                                                onChange={onImageHandler}
                                            />
                                        </Button>
                                    )}
                                    {uploading && (
                                        <Box sx={{ display: 'flex', mt: 1 }}>
                                            <CircularProgress size={24} />
                                        </Box>
                                    )}
                                </ProfileSection>

                                {/* Personal Information */}
                                <FormSection>
                                    <SectionTitle variant="subtitle1">
                                        <PersonAddIcon />
                                        Personal Information
                                    </SectionTitle>

                                    <Grid container spacing={2}>
                                       
                                        <Grid item xs={12} sm={4}>
                                            <TextField
                                                fullWidth
                                                label="First Name"
                                                name="firstName"
                                                size="small"
                                                value={values.firstName || ''}
                                                onChange={handleChange}
                                                required
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={4}>
                                            <TextField
                                                fullWidth
                                                label="Last Name"
                                                name="lastName"
                                                size="small"
                                                value={values.lastName || ''}
                                                onChange={handleChange}
                                                required
                                            />
                                        </Grid>
                                        <Grid item xs={12}>
                                            <TextField
                                                fullWidth
                                                label="Company Position"
                                                name="companyPosition"
                                                size="small"
                                                value={
                                                    values.companyPosition || ''
                                                }
                                                onChange={handleChange}
                                                required
                                                InputProps={{
                                                    startAdornment: (
                                                        <InputAdornment position="start">
                                                            <WorkIcon color="action" />
                                                        </InputAdornment>
                                                    ),
                                                }}
                                            />
                                        </Grid>
                                    </Grid>
                                </FormSection>

                                {/* Contact Information */}
                                <FormSection>
                                    <SectionTitle variant="subtitle1">
                                        <EmailIcon />
                                        Contact Information
                                    </SectionTitle>

                                    <Grid container spacing={2}>
                                        <Grid item xs={12} sm={6}>
                                            <TextField
                                                fullWidth
                                                label="Email Address"
                                                name="email"
                                                size="small"
                                                type="email"
                                                disabled={
                                                    values.email && values._id
                                                }
                                                value={values.email || ''}
                                                onChange={handleChange}
                                                required
                                                helperText={
                                                    values.email && values._id
                                                        ? 'Email cannot be changed'
                                                        : ''
                                                }
                                                InputProps={{
                                                    startAdornment: (
                                                        <InputAdornment position="start">
                                                            <EmailIcon color="action" />
                                                        </InputAdornment>
                                                    ),
                                                }}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <TextField
                                                fullWidth
                                                label="Phone Number"
                                                name="phone"
                                                size="small"
                                                value={values.phone || ''}
                                                onChange={handleChange}
                                                required
                                                InputProps={{
                                                    startAdornment: (
                                                        <InputAdornment position="start">
                                                            <PhoneIcon color="action" />
                                                        </InputAdornment>
                                                    ),
                                                }}
                                            />
                                        </Grid>
                                    </Grid>
                                </FormSection>

                                {/* Role Type (Admin only) */}
                                {isAdminUser && (
                                    <FormSection>
                                        <SectionTitle variant="subtitle1">
                                            <SecurityIcon />
                                            Role Type
                                        </SectionTitle>
                                        <Grid container spacing={2}>
                                            <Grid item xs={12} sm={6}>
                                                <TextField
                                                    select
                                                    fullWidth
                                                    label="Role"
                                                    name="role"
                                                    size="small"
                                                    value={values.role}
                                                    onChange={(e) => {
                                                        const nextRole =
                                                            e.target.value
                                                        handleChange(e)
                                                        if (
                                                            nextRole !==
                                                            'STORE_MANAGER'
                                                        ) {
                                                            setFieldValue(
                                                                'storeIds',
                                                                []
                                                            )
                                                        }
                                                    }}
                                                    helperText={
                                                        ROLE_OPTIONS.find(
                                                            (r) =>
                                                                r.value ===
                                                                values.role
                                                        )?.helperText
                                                    }
                                                >
                                                    {ROLE_OPTIONS.map(
                                                        (role) => (
                                                            <MenuItem
                                                                key={role.value}
                                                                value={
                                                                    role.value
                                                                }
                                                            >
                                                                {role.label}
                                                            </MenuItem>
                                                        )
                                                    )}
                                                </TextField>
                                            </Grid>
                                            {values.role ===
                                                'STORE_MANAGER' && (
                                                <Grid item xs={12} sm={6}>
                                                    <TextField
                                                        select
                                                        fullWidth
                                                        label="Locations"
                                                        size="small"
                                                        value={
                                                            values.storeIds ||
                                                            []
                                                        }
                                                        SelectProps={{
                                                            multiple: true,
                                                            renderValue: (
                                                                selected
                                                            ) => {
                                                                const names =
                                                                    storeOptions
                                                                        .filter(
                                                                            (
                                                                                store
                                                                            ) =>
                                                                                selected.includes(
                                                                                    store?._id
                                                                                )
                                                                        )
                                                                        .map(
                                                                            (
                                                                                store
                                                                            ) =>
                                                                                store?.name
                                                                        )
                                                                return names.length
                                                                    ? names.join(
                                                                          ', '
                                                                      )
                                                                    : 'No location selected'
                                                            },
                                                        }}
                                                        onChange={(e) =>
                                                            setFieldValue(
                                                                'storeIds',
                                                                e.target.value
                                                            )
                                                        }
                                                        helperText={
                                                            storesLoading
                                                                ? 'Loading locations...'
                                                                : 'Select one or more locations for this manager'
                                                        }
                                                    >
                                                        {storeOptions.map(
                                                            (store) => (
                                                                <MenuItem
                                                                    key={
                                                                        store?._id
                                                                    }
                                                                    value={
                                                                        store?._id
                                                                    }
                                                                >
                                                                    <Checkbox
                                                                        checked={(
                                                                            values.storeIds ||
                                                                            []
                                                                        ).includes(
                                                                            store?._id
                                                                        )}
                                                                    />
                                                                    <Typography variant="body2">
                                                                        {formatStoreLabel(
                                                                            store
                                                                        )}
                                                                    </Typography>
                                                                </MenuItem>
                                                            )
                                                        )}
                                                    </TextField>
                                                </Grid>
                                            )}
                                        </Grid>
                                    </FormSection>
                                )}

                                {canConfigureSaRoles && (
                                    <FormSection>
                                        <SectionTitle variant="subtitle1">
                                            <SecurityIcon />
                                            Role Type
                                        </SectionTitle>
                                        <Grid container spacing={2}>
                                            <Grid item xs={12} sm={6}>
                                                {(() => {
                                                    const selectedKey =
                                                        saRoleOptions.some(
                                                            (r) =>
                                                                r.value ===
                                                                values
                                                                    .permission?.[0]
                                                        )
                                                            ? values
                                                                  .permission[0]
                                                            : saRoleOptions[0]
                                                                  ?.value ||
                                                              DEFAULT_SUPER_ADMIN_PERMISSION
                                                    const selectedOption =
                                                        saRoleOptions.find(
                                                            (r) =>
                                                                r.value ===
                                                                selectedKey
                                                        )
                                                    return (
                                                        <TextField
                                                            select
                                                            fullWidth
                                                            label="Role"
                                                            size="small"
                                                            value={selectedKey}
                                                            disabled={
                                                                !isMaster &&
                                                                saRoleOptions.length ===
                                                                    1
                                                            }
                                                            onChange={(e) => {
                                                                const next =
                                                                    saRoleOptions.find(
                                                                        (r) =>
                                                                            r.value ===
                                                                            e
                                                                                .target
                                                                                .value
                                                                    )
                                                                if (!next)
                                                                    return
                                                                setFieldValue(
                                                                    'role',
                                                                    next.role
                                                                )
                                                                setFieldValue(
                                                                    'permission',
                                                                    [
                                                                        ...next.permission,
                                                                    ]
                                                                )
                                                                setFieldValue(
                                                                    'sectionAccess',
                                                                    next.value ===
                                                                        'master'
                                                                        ? fullWriteAccess()
                                                                        : defaultSectionAccessForSecondary()
                                                                )
                                                                setFieldValue(
                                                                    'allowedClientIds',
                                                                    []
                                                                )
                                                            }}
                                                            FormHelperTextProps={{
                                                                component:
                                                                    'div',
                                                                sx: {
                                                                    ml: 0,
                                                                    mt: 1,
                                                                },
                                                            }}
                                                            helperText={
                                                                selectedOption?.helperText && (
                                                                    <Box
                                                                        sx={{
                                                                            display:
                                                                                'flex',
                                                                            alignItems:
                                                                                'flex-start',
                                                                            gap: 1,
                                                                            px: 1.25,
                                                                            py: 1,
                                                                            borderRadius: 1,
                                                                            bgcolor:
                                                                                (
                                                                                    theme
                                                                                ) =>
                                                                                    theme
                                                                                        .palette
                                                                                        .mode ===
                                                                                    'dark'
                                                                                        ? 'rgba(33, 150, 243, 0.12)'
                                                                                        : 'rgba(33, 150, 243, 0.08)',
                                                                            border: '1px solid',
                                                                            borderColor:
                                                                                (
                                                                                    theme
                                                                                ) =>
                                                                                    theme
                                                                                        .palette
                                                                                        .mode ===
                                                                                    'dark'
                                                                                        ? 'rgba(33, 150, 243, 0.35)'
                                                                                        : 'rgba(33, 150, 243, 0.25)',
                                                                        }}
                                                                    >
                                                                        <InfoOutlinedIcon
                                                                            fontSize="small"
                                                                            sx={{
                                                                                color: 'info.main',
                                                                                mt: '2px',
                                                                                flexShrink: 0,
                                                                            }}
                                                                        />
                                                                        <Typography
                                                                            variant="caption"
                                                                            sx={{
                                                                                color: 'text.secondary',
                                                                                lineHeight: 1.5,
                                                                            }}
                                                                        >
                                                                            <Box
                                                                                component="span"
                                                                                sx={{
                                                                                    fontWeight: 600,
                                                                                    color: 'text.primary',
                                                                                    mr: 0.5,
                                                                                }}
                                                                            >
                                                                                {
                                                                                    selectedOption.label
                                                                                }
                                                                                :
                                                                            </Box>
                                                                            {
                                                                                selectedOption.helperText
                                                                            }
                                                                        </Typography>
                                                                    </Box>
                                                                )
                                                            }
                                                        >
                                                            {saRoleOptions.map(
                                                                (role) => (
                                                                    <MenuItem
                                                                        key={
                                                                            role.value
                                                                        }
                                                                        value={
                                                                            role.value
                                                                        }
                                                                    >
                                                                        {
                                                                            role.label
                                                                        }
                                                                    </MenuItem>
                                                                )
                                                            )}
                                                        </TextField>
                                                    )
                                                })()}
                                            </Grid>
                                        </Grid>
                                    </FormSection>
                                )}

                                {canConfigureSaRoles &&
                                    values.permission?.[0] === 'secondary' && (
                                    <FormSection>
                                        <SectionTitle variant="subtitle1">
                                            <SecurityIcon />
                                            Section Access
                                        </SectionTitle>
                                        <Typography
                                            variant="body2"
                                            color="text.secondary"
                                            sx={{ mb: 2 }}
                                        >
                                            Choose which areas of the Super Admin
                                            dashboard this user can open, and
                                            whether access is read only or read
                                            &amp; write.
                                        </Typography>
                                        <Grid container spacing={2}>
                                            {SA_SECTIONS.map((section) => (
                                                <Grid
                                                    item
                                                    xs={12}
                                                    sm={6}
                                                    key={section.key}
                                                >
                                                    <TextField
                                                        select
                                                        fullWidth
                                                        size="small"
                                                        label={section.label}
                                                        value={
                                                            values
                                                                .sectionAccess?.[
                                                                section.key
                                                            ] || 'none'
                                                        }
                                                        onChange={(e) => {
                                                            const nextLevel =
                                                                e.target.value
                                                            setFieldValue(
                                                                'sectionAccess',
                                                                {
                                                                    ...(values.sectionAccess ||
                                                                        {}),
                                                                    [section.key]:
                                                                        nextLevel,
                                                                }
                                                            )
                                                            if (
                                                                section.key ===
                                                                    'clients' &&
                                                                nextLevel ===
                                                                    'none'
                                                            ) {
                                                                setFieldValue(
                                                                    'allowedClientIds',
                                                                    []
                                                                )
                                                            }
                                                        }}
                                                        helperText={
                                                            section.description
                                                        }
                                                    >
                                                        {accessLevelsForSection(
                                                            section.key
                                                        ).map((level) => (
                                                            <MenuItem
                                                                key={
                                                                    level.value
                                                                }
                                                                value={
                                                                    level.value
                                                                }
                                                            >
                                                                {level.label}
                                                            </MenuItem>
                                                        ))}
                                                    </TextField>
                                                </Grid>
                                            ))}
                                        </Grid>

                                        {['read', 'login', 'write'].includes(
                                            values.sectionAccess?.clients
                                        ) && (
                                            <Box sx={{ mt: 2 }}>
                                                <TextField
                                                    select
                                                    fullWidth
                                                    size="small"
                                                    label="Allowed clients"
                                                    SelectProps={{
                                                        multiple: true,
                                                        renderValue: (
                                                            selected
                                                        ) => {
                                                            const ids =
                                                                selected || []
                                                            if (!ids.length) {
                                                                return 'Select clients…'
                                                            }
                                                            const names = ids
                                                                .map((id) => {
                                                                    const club =
                                                                        clientOptions.find(
                                                                            (
                                                                                c
                                                                            ) =>
                                                                                String(
                                                                                    c._id
                                                                                ) ===
                                                                                String(
                                                                                    id
                                                                                )
                                                                        )
                                                                    return (
                                                                        club?.name ||
                                                                        club?.knownAs ||
                                                                        id
                                                                    )
                                                                })
                                                                .filter(Boolean)
                                                            return names.join(
                                                                ', '
                                                            )
                                                        },
                                                    }}
                                                    value={
                                                        values.allowedClientIds ||
                                                        []
                                                    }
                                                    onChange={(e) =>
                                                        setFieldValue(
                                                            'allowedClientIds',
                                                            e.target.value
                                                        )
                                                    }
                                                    helperText={
                                                        clientsLoading
                                                            ? 'Loading clients…'
                                                            : 'Required: choose which clients this secondary admin can open. Leave empty for none.'
                                                    }
                                                >
                                                    {clientOptions.map(
                                                        (club) => (
                                                            <MenuItem
                                                                key={club._id}
                                                                value={String(
                                                                    club._id
                                                                )}
                                                            >
                                                                <Checkbox
                                                                    checked={(
                                                                        values.allowedClientIds ||
                                                                        []
                                                                    ).includes(
                                                                        String(
                                                                            club._id
                                                                        )
                                                                    )}
                                                                />
                                                                <Typography variant="body2">
                                                                    {club.name ||
                                                                        club.knownAs ||
                                                                        club._id}
                                                                </Typography>
                                                            </MenuItem>
                                                        )
                                                    )}
                                                </TextField>
                                            </Box>
                                        )}
                                    </FormSection>
                                )}

                                <Alert severity="info" sx={{ mb: 3 }}>
                                    <AlertTitle>
                                        Password Information
                                    </AlertTitle>
                                    An auto-generated password will be sent to
                                    the specified email address.
                                </Alert>

                                <Box
                                    display="flex"
                                    gap={2}
                                    justifyContent="flex-end"
                                >
                                    <Button
                                        variant="outlined"
                                        onClick={() => setAdminDialog(false)}
                                    >
                                        Cancel
                                    </Button>
                                    <LoadingButton
                                        color="primary"
                                        type="submit"
                                        loading={loading}
                                        startIcon={<SendIcon />}
                                        variant="contained"
                                    >
                                        {isEditing
                                            ? 'Update Admin'
                                            : 'Add Admin'}
                                    </LoadingButton>
                                </Box>
                            </Box>
                        )}
                    </Formik>
                </Box>
            </Dialog>
        )
    }

    const DeleteAdminFormDialog = () => {
        return (
            <Dialog
                open={deleteDialog}
                onClose={() => {
                    setLoading(false)
                    setDeleteDialog(false)
                }}
                keepMounted
                TransitionComponent={Transition}
                PaperProps={{ sx: { borderRadius: 2 } }}
            >
                <DialogHeader>
                    <H4 sx={{ m: 0, color: '#fff' }}>Confirm Deletion</H4>
                    <IconButton
                        onClick={() => {
                            setLoading(false)
                            setDeleteDialog(false)
                        }}
                    >
                        <Icon sx={{ color: '#fff' }}>close</Icon>
                    </IconButton>
                </DialogHeader>

                <DialogContent sx={{ pt: 3 }}>
                    <Box display="flex" alignItems="center" gap={2} mb={2}>
                        <DeleteIcon color="error" sx={{ fontSize: 32 }} />
                        <Typography variant="h6">
                            Delete {initialValues.firstName}{' '}
                            {initialValues.lastName}?
                        </Typography>
                    </Box>
                    <DialogContentText>
                        This action cannot be undone. All data associated with
                        this admin account will be permanently removed from the
                        system.
                    </DialogContentText>
                </DialogContent>

                <DialogActions sx={{ p: 3, gap: 1 }}>
                    <Button
                        variant="outlined"
                        onClick={() => {
                            setInitialValues(INITIAL_STATE)
                            setDeleteDialog(false)
                        }}
                    >
                        Cancel
                    </Button>
                    <LoadingButton
                        color="error"
                        loading={loading}
                        onClick={deleteHandler}
                        startIcon={<DeleteIcon />}
                        variant="contained"
                    >
                        Delete Admin
                    </LoadingButton>
                </DialogActions>
            </Dialog>
        )
    }

    const ResendCredentialsDialog = () => {
        return (
            <Dialog
                open={resendCredentialsDialog}
                onClose={() => {
                    if (loading) return
                    setResendCredentialsDialog(false)
                }}
                keepMounted
                TransitionComponent={Transition}
                PaperProps={{ sx: { borderRadius: 2 } }}
            >
                <DialogHeader>
                    <H4 sx={{ m: 0, color: '#fff' }}>
                        Resend username &amp; password
                    </H4>
                    <IconButton
                        disabled={loading}
                        onClick={() => setResendCredentialsDialog(false)}
                    >
                        <Icon sx={{ color: '#fff' }}>close</Icon>
                    </IconButton>
                </DialogHeader>

                <DialogContent sx={{ pt: 3 }}>
                    <Box display="flex" alignItems="center" gap={2} mb={2}>
                        <VpnKeyIcon color="secondary" sx={{ fontSize: 32 }} />
                        <Typography variant="h6">
                            Reset credentials for {initialValues.firstName}{' '}
                            {initialValues.lastName}?
                        </Typography>
                    </Box>
                    <DialogContentText>
                        {initialValues.firstName} {initialValues.lastName} (
                        {initialValues.email}) will receive an email with their
                        username and a newly generated password. Their current
                        password will stop working immediately.
                    </DialogContentText>
                </DialogContent>

                <DialogActions sx={{ p: 3, gap: 1 }}>
                    <Button
                        variant="outlined"
                        disabled={loading}
                        onClick={() => {
                            setInitialValues(INITIAL_STATE)
                            setResendCredentialsDialog(false)
                        }}
                    >
                        Cancel
                    </Button>
                    <LoadingButton
                        color="primary"
                        loading={loading}
                        onClick={resendCredentialsHandler}
                        startIcon={<VpnKeyIcon />}
                        variant="contained"
                    >
                        Send new password
                    </LoadingButton>
                </DialogActions>
            </Dialog>
        )
    }

    return (
        <StyledCard sx={{ m: 2 }} elevation={3}>
            <Box
                p={3}
                display="flex"
                justifyContent="space-between"
                alignItems="center"
            >
                <Box display="flex" alignItems="center" gap={2}>
                    <SecurityIcon color="primary" sx={{ fontSize: 28 }} />
                    <Box>
                        <H4 sx={{ mb: 0 }}>
                            Authorised Administrators{' '}
                            {ENV === 'QA' ? '(QA)' : ''}
                        </H4>
                        <Typography variant="body2" color="text.secondary">
                            Manage admin accounts and permissions{' '}
                            {ENV === 'QA' ? '(QA)' : ''}
                        </Typography>
                    </Box>
                </Box>
                {canManageAdmins && (
                    <Button
                        variant="contained"
                        color="primary"
                        startIcon={<PersonAddIcon />}
                        onClick={() => {
                            setInitialValues(
                                getCreateInitialValues(canConfigureSaRoles, {
                                    asMaster: isMaster,
                                })
                            )
                            setImageUrl({})
                            setAdminDialog(true)
                        }}
                    >
                        Add Admin
                    </Button>
                )}
            </Box>
            <Divider />
            <CardContent sx={{ p: 0 }}>
                <SupportAdminList />
            </CardContent>

            {DeleteAdminFormDialog()}
            {ResendCredentialsDialog()}
            {AddAdminFormDialog()}

            <Snackbar
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                open={open}
                autoHideDuration={4000}
                onClose={() => setOpen(false)}
            >
                <Alert
                    onClose={() => setOpen(false)}
                    severity={success ? 'success' : 'error'}
                    sx={{ width: '100%' }}
                >
                    {message}
                </Alert>
            </Snackbar>
        </StyledCard>
    )
}

export default AuthorizeAdmin
